// Cache for projectile images
const projectileImageCache = new Map();

function getProjectileImage(url) {
  if (!projectileImageCache.has(url)) {
    const img = new Image();
    img.src = url;
    projectileImageCache.set(url, img);
  }
  return projectileImageCache.get(url);
}

export class Bullet {
  constructor(config) {
    this.x = config.x;
    this.y = config.y;
    this.vx = config.vx || 0;
    this.vy = config.vy;
    this.damage = config.damage || 1;
    this.friendly = config.friendly;
    this.sourceCharacterId = config.sourceCharacterId || null; // For character-specific hit effects

    // Bullet sizes: Allow custom sizes for boss projectiles
    // All increased by 5% for better visibility
    if (config.width && config.height) {
      this.width = config.width;
      this.height = config.height;
    } else if (config.isBossProjectile) {
      this.width = 12; // Increased by 5% from 11px (11 * 1.05 = 11.55 → 12)
      this.height = 12;
    } else {
      // Default sizes: player bullets 15px, enemy bullets 7px
      this.width = config.friendly ? 15 : 7; // Increased by 5%: player 14→15, enemy 6→7
      this.height = config.friendly ? 15 : 7;
    }

    // Render sizes: Visual display sizes (larger than hitboxes)
    // Player projectiles: +20% visual size (15 → 18)
    // Boss projectiles: +25% visual size (12 → 15)
    // Enemy regular projectiles: no scaling (7px)
    if (config.friendly) {
      this.renderWidth = Math.round(this.width * 1.2); // +20% for player
      this.renderHeight = Math.round(this.height * 1.2);
    } else if (config.isBossProjectile) {
      this.renderWidth = Math.round(this.width * 1.25); // +25% for boss
      this.renderHeight = Math.round(this.height * 1.25);
    } else {
      this.renderWidth = this.width; // No scaling for regular enemies
      this.renderHeight = this.height;
    }

    this.color = config.color || (config.friendly ? '#7fd0ff' : '#b966ff'); // Purple for enemy projectiles

    // Character-specific projectile sprite (for player and boss bullets)
    if (config.projectileUrl && config.friendly) {
      this.projectileImage = getProjectileImage(config.projectileUrl);
      this.hasSprite = true;
    } else if (config.sprite && !config.friendly) {
      // Boss/enemy bullets with sprites
      this.projectileImage = getProjectileImage(config.sprite);
      this.hasSprite = true;
    } else {
      this.hasSprite = false;
    }
  }

  update(deltaTime = 16.67) {
    // Bullets move faster than players for better arcade feel
    const moveScale = 0.0015; // ~5-7 pixels/frame for bullets
    this.x += this.vx * moveScale * deltaTime;
    this.y += this.vy * moveScale * deltaTime;
  }

  isOffScreen(canvasHeight) {
    return this.y < -20 || this.y > canvasHeight + 20;
  }

  render(ctx) {
    ctx.save();

    // Calculate rotation angle based on velocity direction
    // For boss projectiles, rotate so the tip leads in the direction of travel
    const angle = Math.atan2(this.vy, this.vx);

    // Translate to projectile position and rotate
    ctx.translate(this.x, this.y);
    ctx.rotate(angle + Math.PI / 2); // +90° so sprite points in direction of travel

    if (this.hasSprite && this.projectileImage && this.projectileImage.complete) {
      // Preserve aspect ratio for projectile sprites
      const imgWidth = this.projectileImage.naturalWidth || this.projectileImage.width;
      const imgHeight = this.projectileImage.naturalHeight || this.projectileImage.height;

      let renderWidth = this.renderWidth;
      let renderHeight = this.renderHeight;

      if (imgWidth > 0 && imgHeight > 0) {
        const aspectRatio = imgWidth / imgHeight;

        if (aspectRatio > 1) {
          // Wider than tall - fit to width
          renderHeight = this.renderWidth / aspectRatio;
        } else if (aspectRatio < 1) {
          // Taller than wide - fit to height
          renderWidth = this.renderHeight * aspectRatio;
        }
        // If aspectRatio === 1, keep square dimensions
      }

      // Render character-specific projectile sprite (friendly or enemy)
      // Now centered at (0, 0) due to translate, rotated in direction of travel
      ctx.shadowColor = this.friendly ? '#7fd0ff' : this.color;
      ctx.shadowBlur = this.friendly ? 8 : 12; // Stronger glow for enemy projectiles
      ctx.drawImage(
        this.projectileImage,
        -renderWidth / 2,
        -renderHeight / 2,
        renderWidth,
        renderHeight
      );
      ctx.shadowBlur = 0;
    } else {
      // Fallback: Render colored shape using render sizes
      ctx.fillStyle = this.color;

      if (this.friendly) {
        ctx.shadowColor = '#7fd0ff';
        ctx.shadowBlur = 5;
      } else {
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 8; // Stronger glow for enemy bullets
      }

      // Enemy bullets: diamond shape (rotated square for pointed look)
      // Player bullets: square
      if (!this.friendly) {
        // Draw diamond shape - more pointed/menacing for enemy projectiles
        const halfWidth = this.renderWidth / 2;
        const halfHeight = this.renderHeight / 2;

        ctx.beginPath();
        ctx.moveTo(0, -halfHeight); // Top point
        ctx.lineTo(halfWidth, 0); // Right point
        ctx.lineTo(0, halfHeight); // Bottom point
        ctx.lineTo(-halfWidth, 0); // Left point
        ctx.closePath();
        ctx.fill();
      } else {
        // Player bullets remain square
        ctx.fillRect(
          -this.renderWidth / 2,
          -this.renderHeight / 2,
          this.renderWidth,
          this.renderHeight
        );
      }

      ctx.shadowBlur = 0;
    }

    ctx.restore();
  }
}
